import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";

export default function AdminLogin() {
  const { login } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-[#131327] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-subtle outline-none focus:border-accent/60 transition-colors";

  return (
    <div className="min-h-screen bg-[#0B0B14] flex items-center justify-center p-4 relative overflow-hidden" data-testid="admin-login-page">
      <div className="ambient-glow w-[520px] h-[320px] top-0 left-1/2 -translate-x-1/2 opacity-50" />
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-xs text-subtle hover:text-white transition-colors" data-testid="admin-login-back-link">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to site
      </Link>
      <div className="relative w-full max-w-sm glass rounded-3xl p-8 sm:p-10">
        <div className="flex flex-col items-center text-center mb-8">
          <Logo settings={settings} size={56} className="mb-4" />
          <h1 className="font-display text-xl font-bold text-white tracking-tight">Admin Panel</h1>
          <p className="font-mono-label text-[9px] text-accent-bright mt-2">{settings?.brand?.name || "Scalovix Media Agency"}</p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="font-mono-label text-[10px] text-subtle block mb-2" htmlFor="admin-email">Email</label>
            <input id="admin-email" type="email" autoComplete="email" className={inputCls} placeholder="admin@scalovix.media" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" />
          </div>
          <div>
            <label className="font-mono-label text-[10px] text-subtle block mb-2" htmlFor="admin-password">Password</label>
            <input id="admin-password" type="password" autoComplete="current-password" className={inputCls} placeholder="••••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input" />
          </div>
          {error && <p className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3.5 py-2.5" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-accent hover:bg-accent-bright disabled:opacity-60 text-white font-semibold py-3 text-sm shadow-[0_0_24px_rgba(124,58,237,0.4)] transition-all duration-300"
            data-testid="admin-login-submit-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {busy ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <p className="mt-6 text-[11px] text-subtle text-center leading-relaxed">
          Protected area. Failed attempts are rate-limited.<br />Passwords are never stored in plain text.
        </p>
      </div>
    </div>
  );
}
